import { useSectionAccess } from "../auth/useSectionAccess";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { formatDate, formatDateShort, formatDateTime, formatMoney } from "../lib/format";
import { CardsSkeleton, Spinner } from "../components/Loading";
import { KanbanColumn, boardScroll as tablaScroll } from "../components/KanbanColumn";

/** Koliko kartica ide u vrh jedne kolone. Ostatak kaže brojač, ne spisak. */
const COLUMN_SIZE = 25;
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { offersApi, type Offer } from "../api/offers";
import { offerStagesApi, type OfferStage } from "../api/offerStages";
import { useToast } from "../components/Toast";
import { Select } from "../components/Select";
import Ic from "../components/icons.jsx";
import { mailApi } from "../api/mail";
import type { ApiError } from "../api/client";
import {
  groupByStage, stageColor, isOverdue, ownerInitials, cardSubtitle,
  type BoardSort, type BoardColumn,
} from "./offersBoard";
import { LoadFailed } from "../components/LoadFailed";
import { OffersViewSwitcher } from "../components/offers/OffersViewSwitcher";
import { OffersFilterBar, FilterField, emptyState } from "../components/offers/OffersFilterBar";
import {
  hasActiveFilter, distinctCountries, distinctOwners,
  EMPTY_FILTER_CRITERIA, type OfferFilterCriteria,
} from "./offersFilter";
import { localizedName } from "../lib/localizedName";

const money = (n: number | null | undefined, cur: string | null | undefined) =>
  formatMoney(n, cur);

const SORTS: BoardSort[] = ["modified", "due"];

/**
 * Kanban board — default /offers route. Columns are the active offer_stage rows in position order;
 * cards are HTML5-draggable between any two columns (free movement, no FSM —
 * "slobodno kao CRM"). Filters mirror OffersPage (the list view) via the
 * same offersFilter.ts criteria shape, so the two views present the same
 * filter bar; grouping/sort/search predicate logic lives in offersBoard.ts
 * so it's unit-testable without mounting React.
 *
 * Modeled on TmTasksPage's KanbanBoard (the closest existing precedent) but
 * simpler: no client-side FSM to gate drops, and the column count is
 * dynamic (admin-configurable stages) rather than a fixed 5, so columns
 * scroll horizontally instead of sitting in a fixed grid.
 */
export default function OffersBoardPage() {
  const { canWrite } = useSectionAccess("offers");
  const { t } = useTranslation();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [offers, setOffers] = useState<Offer[] | null>(null);
  /** Poruka kvara; prazna tabla i neuspelo učitavanje ne smeju da izgledaju isto. */
  const [greska, setGreska] = useState<string | null>(null);
  /** Brojač koji se poveća na „pokušaj ponovo" i time ponovo pokrene učitavanje. */
  const [osvezi, setOsvezi] = useState(0);
  // offerId -> last send; drives the "mailed" marker on a card.
  const [mailed, setMailed] = useState<Record<string, string>>({});
  const [stages, setStages] = useState<OfferStage[] | null>(null);
  const [criteria, setCriteria] = useState<OfferFilterCriteria>(() => ({
    ...EMPTY_FILTER_CRITERIA,
    search: searchParams.get("q") ?? "",
  }));
  const [sort, setSort] = useState<BoardSort>("modified");
  const [draggingOffer, setDraggingOffer] = useState<Offer | null>(null);
  const [ukupnoPoFazi, setUkupnoPoFazi] = useState<Record<string, number>>({});
  /** Zbir po fazi i valuti, sa servera. Kolona prikazuje samo vrh, pa se iz nje ne može sabrati. */
  const [iznosPoFazi, setIznosPoFazi] = useState<Record<string, Record<string, number>>>({});
  const [evriPoFazi, setEvriPoFazi] = useState<Record<string, number>>({});
  const [bezKursaPoFazi, setBezKursaPoFazi] = useState<Record<string, number>>({});
  /**
   * Koliko je kartica traženo po fazi. Na tabli „strana" nema smisla: kolone imaju vrlo
   * različit broj ponuda, pa se ide „prikaži još" po koloni, a koliko ih ukupno ima kaže
   * brojač u zaglavlju.
   */
  const [trazenoPoFazi, setTrazenoPoFazi] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void mailApi.mailedOffers().then(setMailed).catch(() => setMailed({}));
  }, [toast]);

  /**
   * Svaka kolona puni sebe.
   *
   * Tabla je ranije učitavala sve ponude i grupisala ih u pregledaču. Sa istorijom iz starog
   * sistema to je sto hiljada kartica, a nijedno oko ih ne gleda: vidi se vrh svake kolone.
   * Zato se po fazi traži prvih {@link COLUMN_SIZE}, a koliko ih ukupno ima kaže brojač sa
   * servera — inače bi zaglavlje kolone pisalo dvadeset pet, koliko je slučajno stiglo.
   */
  useEffect(() => {
    if (!stages) return;
    let ziv = true;
    setBusy(true);
    const zajednicko = {
      country: criteria.country || undefined,
      dateFrom: criteria.dateFrom || undefined,
      dateTo: criteria.dateTo || undefined,
      ownerId: criteria.ownerId || undefined,
      q: criteria.search.trim() || undefined,
    };
    const posao = async () => {
      // Brojači i zbir idu sa istim kriterijumima kao kartice. Bez toga zaglavlje gleda ceo
      // skup a kolona filtrirani, pa se pri promeni filtera kartice menjaju a iznos ne.
      void offersApi.counts(zajednicko)
        .then((r) => { setIznosPoFazi(r.amounts ?? {}); setEvriPoFazi(r.eur ?? {}); setBezKursaPoFazi(r.noRate ?? {}); })
        .catch(() => { setIznosPoFazi({}); setEvriPoFazi({}); setBezKursaPoFazi({}); });
      const strane = await Promise.all(
        stages.map((st) =>
          offersApi.list({ ...zajednicko, stageId: st.id, size: trazenoPoFazi[st.id] ?? COLUMN_SIZE })),
      );
      if (!ziv) return;
      setOffers(strane.flatMap((r) => r.content));
      setUkupnoPoFazi(Object.fromEntries(stages.map((st, i) => [st.id, strane[i].totalElements])));
      setGreska(null);
    };
    const id = setTimeout(() => {
      posao()
        .catch((e: ApiError) => { if (ziv) { toast.error(e.message); setGreska(e.message); } })
        .finally(() => { if (ziv) setBusy(false); });
    }, criteria.search ? 450 : 0);
    return () => { ziv = false; clearTimeout(id); };
  }, [stages, criteria.country, criteria.dateFrom, criteria.dateTo, criteria.ownerId, criteria.search,
      trazenoPoFazi, toast, osvezi]);

  useEffect(() => {
    offerStagesApi.list()
      .then((v) => { setStages(v); setGreska(null); })
      .catch((e: ApiError) => { toast.error(e.message); setStages([]); setGreska(e.message); });
  }, [toast, osvezi]);

  // Same "q"-only URL-sync idiom as OffersPage — see OffersViewSwitcher's
  // docblock for why only search (not stage/country/date/owner) round-trips
  // across the board<->list navigation.
  const setSearch = (v: string) => {
    setCriteria((c) => ({ ...c, search: v }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v) next.set("q", v); else next.delete("q");
      return next;
    }, { replace: true });
  };

  const countries = useMemo(() => distinctCountries(offers ?? []), [offers]);
  const owners = useMemo(() => distinctOwners(offers ?? []), [offers]);
  const showOwnerFilter = owners.length > 1;
  // Baza je već filtrirala i ograničila svaku kolonu.
  const filtered = offers ?? [];
  const filtersActive = hasActiveFilter(criteria);
  const columns = useMemo(
    () => (stages ? groupByStage(filtered, stages, sort) : []),
    [filtered, stages, sort],
  );

  const setDateFrom = (v: string) => { setCriteria((c) => ({ ...c, dateFrom: v })); };
  const setDateTo = (v: string) => { setCriteria((c) => ({ ...c, dateTo: v })); };
  const clearFilters = () => {
    setCriteria({ ...EMPTY_FILTER_CRITERIA, search: criteria.search });
  };

  /**
   * Optimistic drag&drop move. Card jumps to the target column immediately;
   * on API failure we roll the local state back and surface the error
   * (mirrors TmTasksPage's moveTaskTo). No client-side FSM check: the pipeline
   * allows movement between any two stages, so the only guard needed is
   * "don't no-op-transition into the column the card is already in".
   */
  async function moveOfferTo(offer: Offer, toStage: OfferStage) {
    setDraggingOffer(null);
    if (!canWrite || offer.fromErp || offer.stage?.id === toStage.id) return;
    const prevStage = offer.stage;
    setOffers((prev) => prev?.map((o) => (o.id === offer.id ? { ...o, stage: toStage } : o)) ?? null);
    try {
      const updated = await offersApi.transition(offer.id, toStage.id);
      setOffers((prev) => prev?.map((o) => (o.id === offer.id ? updated : o)) ?? null);
    } catch (err) {
      toast.error((err as ApiError).message);
      setOffers((prev) => prev?.map((o) => (o.id === offer.id ? { ...o, stage: prevStage } : o)) ?? null);
    }
  }

  const loading = offers === null || stages === null;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label="Breadcrumb">
            <span>{t("offers.crumb.commercial")}</span>
            <span>{t("offers.list.title")}</span>
          </nav>
          <h1>{t("offers.list.title")}</h1>
          <div className="sub">{t("offers.list.subtitle")}</div>
        </div>
        <div className="page-actions">
          {canWrite && <Link to="/offers/new" className="btn primary"><Ic.plus /> {t("offers.list.new")}</Link>}
        </div>
      </div>

      <OffersViewSwitcher active="board" search={criteria.search} />
      {busy && offers !== null && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--ink-3)", fontSize: 12.5 }}>
          <Spinner size={14} /> {t("common.loading")}
        </div>
      )}

      {offers != null && offers.length > 0 && (
        <OffersFilterBar
          criteria={criteria}
          onSearchChange={setSearch}
          onStageChange={(v) => setCriteria((c) => ({ ...c, stageId: v }))}
          onCountryChange={(v) => setCriteria((c) => ({ ...c, country: v }))}
          onOwnerChange={(v) => setCriteria((c) => ({ ...c, ownerId: v }))}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          stages={stages ?? []}
          countries={countries}
          owners={owners}
          showOwnerFilter={showOwnerFilter}
          shownCount={filtered.length}
          totalCount={offers.length}
          filtersActive={filtersActive}
          onClear={clearFilters}
          extra={
            <FilterField label={t("offers.board.sortLabel")}>
              <Select<BoardSort>
                value={sort}
                onChange={setSort}
                ariaLabel={t("offers.board.sortLabel")}
                style={{ width: 170 }}
                options={SORTS.map((s) => ({ value: s, label: t(`offers.board.sort.${s === "modified" ? "updated" : "due"}`) }))}
              />
            </FilterField>
          }
        />
      )}

      {greska ? (
        <LoadFailed message={greska} onRetry={() => { setGreska(null); setOsvezi((n) => n + 1); }} />
      ) : loading ? (
        <CardsSkeleton count={8} label={t("common.loading")} />
      ) : offers.length === 0 ? (
        <div style={emptyState}>
          <p style={{ margin: 0, color: "var(--ink-3)" }}>{t("offers.list.empty.title")}</p>
          <p style={{ margin: "6px 0 0", color: "var(--ink-4)", fontSize: 12 }}>{t("offers.list.empty.hint")}</p>
          {canWrite && <Link to="/offers/new" className="btn primary" style={{ marginTop: 14 }}><Ic.plus /> {t("offers.list.new")}</Link>}
        </div>
      ) : (
        <div style={tablaScroll}>
          {columns.map((col) => (
            <BoardColumnView
              key={col.stage.id}
              column={col}
              ukupno={ukupnoPoFazi[col.stage.id]}
              iznosi={iznosPoFazi[col.stage.id]}
              uEvrima={evriPoFazi[col.stage.id]}
              bezKursa={bezKursaPoFazi[col.stage.id]}
              naJos={() => setTrazenoPoFazi((p) => ({
                ...p,
                [col.stage.id]: (p[col.stage.id] ?? COLUMN_SIZE) + COLUMN_SIZE,
              }))}
              mailed={mailed}
              dragging={draggingOffer}
              onDragStart={setDraggingOffer}
              onDragEnd={() => setDraggingOffer(null)}
              onDrop={(o) => void moveOfferTo(o, col.stage)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BoardColumnView({
  ukupno,
  iznosi,
  uEvrima,
  bezKursa,
  naJos,
  column, dragging, mailed, onDragStart, onDragEnd, onDrop,
}: {
  column: BoardColumn<OfferStage, Offer>;
  dragging: Offer | null;
  mailed: Record<string, string>;
  onDragStart: (o: Offer) => void;
  onDragEnd: () => void;
  onDrop: (o: Offer) => void;
  /** Koliko ponuda faza stvarno ima. Kolona prikazuje samo vrh, pa bi njena dužina lagala. */
  ukupno?: number;
  /** Zbir po valuti, sa servera. Iz istog razloga se ne računa iz učitanih kartica. */
  iznosi?: Record<string, number>;
  /** Isti zbir u evrima, preračunat po kursu na dan ponude. */
  uEvrima?: number;
  /** Offers of the stage in a currency without any exchange rate, left out of the euro sum. */
  bezKursa?: number;
  /** Traži sledeću porciju kartica za ovu kolonu. */
  naJos: () => void;
}) {
  const { t, i18n } = useTranslation();
  const color = stageColor(column.stage);
  const stageName = localizedName(column.stage, i18n.resolvedLanguage ?? "en");
  const canDrop = useSectionAccess("offers").canWrite && !!dragging && dragging.stage?.id !== column.stage.id;
  const jezik = i18n.resolvedLanguage ?? "sr";

  return (
    <KanbanColumn
      naziv={stageName}
      boja={color}
      broj={ukupno ?? column.offers.length}
      eur={uEvrima}
      poValutama={iznosi}
      bezKursa={bezKursa}
      jezik={jezik}
      moze={canDrop}
      onDrop={() => { if (dragging) onDrop(dragging); }}
      opis={t("offers.board.columnAria", {
        name: stageName,
        count: ukupno ?? column.offers.length,
        sum: uEvrima ? money(uEvrima, "EUR") : "—",
      })}
    >
        {column.offers.length === 0 ? (
          <div style={emptyCol}>—</div>
        ) : (
          column.offers.map((offer) => (
            <OfferCard
              key={offer.id}
              offer={offer}
              mailedAt={mailed[offer.id]}
              dragging={dragging?.id === offer.id}
              onDragStart={() => onDragStart(offer)}
              onDragEnd={onDragEnd}
            />
          ))
        )}
        {/* Kolona prikazuje vrh spiska; koliko ih ukupno ima pise u zaglavlju. */}
        {ukupno != null && column.offers.length < ukupno && (
          <button type="button" className="btn sm" style={jos} onClick={naJos}>
            {t("offers.board.more", {
              shown: column.offers.length,
              total: ukupno,
              defaultValue: "Prikazi jos ({{shown}} od {{total}})",
            })}
          </button>
        )}
    </KanbanColumn>
  );
}

function OfferCard({
  offer, dragging, mailedAt, onDragStart, onDragEnd,
}: {
  offer: Offer;
  dragging?: boolean;
  mailedAt?: string;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const { canWrite } = useSectionAccess("offers");
  const { t } = useTranslation();
  const overdue = isOverdue(offer);
  const subtitle = cardSubtitle(offer);
  return (
    <Link to={`/offers/${offer.id}`} style={{ textDecoration: "none", color: "inherit" }}>
      <article
        draggable={canWrite && !offer.fromErp}
        onDragStart={(e) => {
          e.dataTransfer.setData("text/plain", offer.id);
          e.dataTransfer.effectAllowed = "move";
          onDragStart?.();
        }}
        onDragEnd={onDragEnd}
        style={{
          ...offerCard,
          ...(overdue ? { boxShadow: "inset 3px 0 0 var(--err), var(--shadow-1)" } : {}),
          ...(dragging ? { opacity: 0.5, transform: "scale(0.98)" } : {}),
        }}
      >
        <div style={naslovKartice} title={offer.customerName}>{offer.customerName}</div>
        {subtitle && <div style={podnaslov} title={subtitle}>{subtitle}</div>}

        <div style={iznosKartice}>{money(offer.displayAmount, offer.currency)}</div>

        {offer.tags.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {offer.tags.map((tag) => (
              <span
                key={tag.id}
                style={{
                  background: tag.color ? `color-mix(in srgb, ${tag.color} 18%, var(--surface))` : "var(--surface-muted)",
                  color: tag.color ? "var(--ink)" : "var(--ink-2)",
                  border: `1px solid ${tag.color ?? "var(--border)"}`,
                  padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600,
                }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}

        {mailedAt && (
          // Činjenica da je ponuda zaista poslata kupcu, ništa više.
          <div style={poslato} title={formatDateTime(mailedAt)}>
            <Ic.send style={{ width: 12, height: 12, flex: "none" }} />
            {t("offers.board.mailed", { when: formatDate(mailedAt), defaultValue: "Poslato {{when}}" })}
          </div>
        )}

        {/* Jedno podnožje, ne dva: ko je nosilac, koji je broj i dokad važi stoje u istom redu. */}
        <footer style={podnozje}>
          <span title={offer.ownerName ?? undefined} aria-hidden style={ownerAvatar}>
            {ownerInitials(offer.ownerName)}
          </span>
          <span style={brojKartice} title={offer.offerNumber}>{offer.offerNumber}</span>
          {offer.dueAt ? (
            <span style={{ ...rokKartice, ...(overdue ? { color: "var(--err)", fontWeight: 600 } : {}) }}>
              {fmtDue(offer.dueAt, t)}
            </span>
          ) : (
            <span style={rokKartice}>{fmtModified(offer.updatedAt)}</span>
          )}
        </footer>
      </article>
    </Link>
  );
}

/** "today"/"tomorrow"/"yesterday"/"Nd ago", falling back to a short date further out — mirrors TmTasksPage's fmtDue idiom. */
function fmtDue(iso: string, t: TFunction): string {
  const d = new Date(iso);
  const today = new Date(new Date().toDateString());
  const dueDay = new Date(d.toDateString());
  const diff = Math.round((dueDay.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return t("offers.board.due.today");
  if (diff === 1) return t("offers.board.due.tomorrow");
  if (diff === -1) return t("offers.board.due.yesterday");
  if (diff < 0) return t("offers.board.due.daysAgo", { count: Math.abs(diff) });
  return formatDateShort(d);
}

function fmtModified(iso: string): string {
  return formatDateShort(iso);
}

const jos: CSSProperties = { width: "100%", marginTop: 4 };
const emptyCol: CSSProperties = { color: "var(--ink-4)", fontSize: 12, textAlign: "center", padding: "16px 0" };
const offerCard: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 10,
  // Razmak drži omotač. Dok ga je nosilo svako dete svojim `marginTop` (3, 6, 5, 6, 8, 4),
  // kartica je imala šest različitih razmaka i delovala razbacano.
  display: "flex", flexDirection: "column", gap: 6, boxShadow: "var(--shadow-1)",
  cursor: "grab", transition: "transform .12s ease, box-shadow .12s ease",
};

/** Naziv kupca sme u dva reda, pa se seče: duže ime ne rasteže karticu preko kolone. */
const naslovKartice: CSSProperties = {
  fontWeight: 600, color: "var(--ink)", fontSize: 13, lineHeight: 1.3,
  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
};
const podnaslov: CSSProperties = {
  fontSize: 11.5, color: "var(--ink-3)", lineHeight: 1.3,
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const iznosKartice: CSSProperties = {
  fontSize: 14, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums",
};
const poslato: CSSProperties = {
  display: "flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "var(--ok)",
};
const podnozje: CSSProperties = {
  display: "flex", alignItems: "center", gap: 6,
  paddingTop: 6, borderTop: "1px solid var(--border)",
};
const brojKartice: CSSProperties = {
  fontFamily: "var(--mono)", fontSize: 10, color: "var(--ink-4)",
  flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const rokKartice: CSSProperties = { fontSize: 10.5, color: "var(--ink-3)", flex: "none" };
const ownerAvatar: CSSProperties = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 22, height: 22, borderRadius: "50%", background: "var(--brand-primary)",
  color: "var(--on-primary)", fontSize: 9.5, fontWeight: 700, flex: "none",
};
